import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks/useAppDispatch";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";
import { RootState } from "../../../redux/store";
import { generateGetURL } from "../../../services/mediaApiService";
import placeHolderImage from "../../../assets/placeholders/picture.jpg";
import Logo from "../../../components/Logo/Logo";
import { isImageDarkOrClear } from "../../../utils/colors";
import LanguageSwitcher from "../../../components/LanguageSwitcher/LanguageSwitcher";

const LOCALE = "modules_Guests_components_CoverComponent";

interface CoverComponentProps {
  giftTableID: string;
}

const CoverComponent: React.FC<CoverComponentProps> = ({ giftTableID }) => {
  const { t } = useTranslation(LOCALE);
  const {
    profilePicture,
    thumbnailSmall,
    messageToGuests,
    amphitryon,
    eventType,
  } = useAppSelector((state: RootState) => state.guestGiftTable);

  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [, setLogoColor] = useState<string>("#FFFFFF");

  useEffect(() => {
    const fetchProfileImage = async () => {
      const imageID = thumbnailSmall || profilePicture;
      if (imageID) {
        try {
          const media = await generateGetURL(imageID);
          setProfileImageUrl(media.signedURL);

          // Determine image luminance for logo color
          const luminanceResult = await isImageDarkOrClear(media.signedURL);
          setLogoColor(luminanceResult === "dark" ? "#FFFFFF" : "#000000");
        } catch (error) {
          console.error("Error fetching profile picture:", error);
          setProfileImageUrl(null);
          setLogoColor("#FFFFFF"); // Default to white
        }
      }
    };

    fetchProfileImage();
  }, [profilePicture, thumbnailSmall]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center text-white px-6 py-10 md:px-20 relative"
      style={{
        backgroundImage: `url(${profileImageUrl || placeHolderImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Content */}
      <div className="relative z-10 text-center space-y-12 max-w-xl">
        {/* Logo */}
        <div>
          <Logo className="w-32 h-32 md:w-48 md:h-48 mx-auto" />
        </div>

        {/* Welcome Message */}
        <div className="space-y-6">
          <h1 className="text-3xl md:text-5xl font-bold leading-snug">
            ¡{amphitryon} {t(`cover.eventMessage.${eventType}`)}!
          </h1>
          <blockquote className="italic text-xl md:text-2xl text-gray-300 font-serif">
            "{messageToGuests || t("cover.defaultMessage")}"
          </blockquote>
        </div>

        {/* Button */}
        <button
          onClick={() =>
            (window.location.href = `/guests/gifttable/${giftTableID}`)
          }
          className="bg-primary text-white px-10 py-4 rounded-lg hover:opacity-90 transition text-lg md:text-xl"
        >
          {t("cover.goToGiftTableButton")}
        </button>
      </div>

      {/* Language Switcher Button at Bottom */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
        <LanguageSwitcher className="text-white" />
      </div>
    </div>
  );
};

export default withTranslations(LOCALE)(CoverComponent);
